import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { useLocalizedPath } from "@/hooks/useLocalizedPath";
import { useTranslation } from "react-i18next";
import { useLocationTranslation } from "@/hooks/useLocationTranslation";
import { Button } from "@/components/ui/button";
import { SimpleAnimatedNumber } from "@/components/SimpleAnimatedNumber";
import { useInViewOnce } from "@/hooks/useInViewOnce";
import {
  ArrowRight,
  Truck,
  Package,
  Globe,
  Compass,
  CalendarDays,
} from "lucide-react";
import { cn } from "@/lib/utils";

const LivePlatformActivity = () => {
  const { t } = useTranslation();
  const { localePath } = useLocalizedPath();
  const { translateCity } = useLocationTranslation();
  const { ref: sectionRef, isInView } = useInViewOnce({ threshold: 0.15 });
  const [currentActivity, setCurrentActivity] = useState(0);
  const [activityVisible, setActivityVisible] = useState(true);
  const scrollRef = useRef<HTMLDivElement>(null);
  const [activeSlide, setActiveSlide] = useState(0);

  const popularRoutes = [
    { from: `🇲🇦 ${translateCity("Casablanca")}`, to: `🇪🇸 ${translateCity("Madrid")}`, info: t('liveActivity.routes.couriersThisWeek', { count: 3 }) },
    { from: `🇲🇦 ${translateCity("Tangier")}`, to: `🇫🇷 ${translateCity("Paris")}`, info: t('liveActivity.routes.tripsScheduled', { count: 5 }) },
    { from: `🇲🇦 ${translateCity("Marrakech")}`, to: `🇧🇪 ${translateCity("Brussels")}`, info: t('liveActivity.routes.couriersDeparting', { count: 2 }) },
    { from: `🇲🇦 ${translateCity("Rabat")}`, to: `🇳🇱 ${translateCity("Amsterdam")}`, info: t('liveActivity.routes.activeTrips', { count: 4 }) },
  ];

  const networkStats = [
    { icon: Truck, label: t('liveActivity.stats.couriersOnRoad'), value: 127, color: "text-blue-500" },
    { icon: Package, label: t('liveActivity.stats.shipmentRequests'), value: 14, color: "text-green-500" },
    { icon: Globe, label: t('liveActivity.stats.activeRoutes'), value: 64, color: "text-purple-500" },
    { icon: Compass, label: t('liveActivity.stats.citiesConnected'), value: 41, color: "text-yellow-500" },
  ];

  const departingSoon = [
    { from: translateCity("Casablanca"), to: translateCity("Madrid"), days: 2 },
    { from: translateCity("Tangier"), to: translateCity("Paris"), days: 3 },
    { from: translateCity("Rabat"), to: translateCity("Brussels"), days: 4 },
  ];

  const activityFeedItems = [
    t('liveActivity.feed.shipmentBooked', { route: `${translateCity("Casablanca")} → ${translateCity("Madrid")}` }),
    t('liveActivity.feed.newTrip', { route: `${translateCity("Paris")} → ${translateCity("Tangier")}` }),
    t('liveActivity.feed.shipmentDelivered', { route: `${translateCity("Tangier")} → ${translateCity("Brussels")}` }),
    t('liveActivity.feed.newCourier', { city: translateCity("Rabat") }),
  ];

  // Activity feed rotation
  useEffect(() => {
    const interval = setInterval(() => {
      setActivityVisible(false);
      setTimeout(() => {
        setCurrentActivity((i) => (i + 1) % activityFeedItems.length);
        setActivityVisible(true);
      }, 300);
    }, 3500);
    return () => clearInterval(interval);
  }, [activityFeedItems.length]);

  // Handle mobile slide scroll tracking
  const handleScroll = () => {
    if (!scrollRef.current) return;
    const el = scrollRef.current;
    const slideWidth = el.offsetWidth;
    const scrollPos = Math.abs(el.scrollLeft); // abs for RTL
    const idx = Math.round(scrollPos / slideWidth);
    setActiveSlide(Math.min(idx, 2));
  };

  const mobileCards = [
    // Card 1: Popular Routes
    <div key="routes" className="glass p-5 min-w-full snap-center shrink-0">
      <h3 className="font-display font-bold text-white text-base mb-4">{t('liveActivity.popularRoutes')}</h3>
      <div className="space-y-3">
        {popularRoutes.map((r, i) => (
          <div key={i} className="p-3 bg-white/[0.04] border border-white/[0.06]">
            <div className="flex flex-wrap items-center gap-2 text-sm font-semibold text-white">
              <span className="break-words">{r.from}</span>
              <ArrowRight className="h-3 w-3 text-[var(--color-orange)]" />
              <span className="break-words">{r.to}</span>
            </div>
            <p className="text-xs text-white/40 mt-1">{r.info}</p>
          </div>
        ))}
      </div>
    </div>,
    // Card 2: Network Activity
    <div key="network" className="glass p-5 min-w-full snap-center shrink-0">
      <h3 className="font-display font-bold text-white text-base mb-4">{t('liveActivity.networkActivity')}</h3>
      <div className="space-y-5">
        {networkStats.map((s, i) => (
          <div key={i} className="flex items-center gap-4">
            <div className="icon-tile">
              <s.icon className={cn("h-5 w-5", s.color)} strokeWidth={1.7} />
            </div>
            <div>
              <div className="text-2xl font-bold text-white">
                {isInView ? (
                  <SimpleAnimatedNumber value={s.value} endTimeMs={2000} delayMs={200 + i * 150} />
                ) : "0"}
              </div>
              <p className="text-xs text-white/40 mt-0.5">{s.label}</p>
            </div>
          </div>
        ))}
      </div>
    </div>,
    // Card 3: Trips Departing
    <div key="trips" className="glass p-5 min-w-full snap-center shrink-0">
      <h3 className="font-display font-bold text-white text-base mb-4">{t('liveActivity.tripsDeparting')}</h3>
      <div className="space-y-3">
        {departingSoon.map((trip, i) => (
          <div key={i} className="p-3 bg-white/[0.04] border border-white/[0.06] flex items-center gap-3">
            <CalendarDays className="h-4 w-4 text-[var(--color-orange)] shrink-0" />
            <div>
              <div className="text-sm font-semibold text-white">{trip.from} → {trip.to}</div>
              <p className="text-xs text-white/40">{t('liveActivity.departingIn', { days: trip.days })}</p>
            </div>
          </div>
        ))}
      </div>
    </div>,
  ];

  return (
    <section
      ref={sectionRef as React.RefObject<HTMLElement>}
      className="py-12 md:py-32"
      style={{ background: "var(--color-navy)" }}
    >
      <div className="container max-w-5xl">
        {/* Header */}
        <div className="text-center mb-10 md:mb-20">
          <span className="section-badge-dark">{t('liveActivity.badge')}</span>
          <h2 className="font-display section-heading text-white mt-6">
            {t('liveActivity.heading')}
          </h2>
          <p className="text-white/50 mt-4 max-w-xl mx-auto text-base sm:text-lg leading-relaxed">
            {t('liveActivity.subtitle')}
          </p>
          <div className="flex justify-center mt-6">
            <div className="h-[2px] w-[120px] relative overflow-hidden" style={{ background: "var(--color-orange)" }}>
              <div
                className="absolute inset-0"
                style={{
                  background: "linear-gradient(90deg, transparent, rgba(242,100,25,0.6), transparent)",
                  animation: "accentGlow 2s ease-in-out infinite",
                }}
              />
            </div>
          </div>
          <div className="flex justify-center mt-4">
            <span className="text-xs text-white/40 flex items-center gap-1.5">
              <span className="w-2 h-2 bg-green-500 rounded-full inline-block animate-pulse" />
              {t('liveActivity.updatedRecently')}
            </span>
          </div>
        </div>

        {/* Desktop 3-column grid */}
        <div className="hidden md:grid grid-cols-3 gap-6">
          <div className="glass p-4 sm:p-6">
            <h3 className="font-display font-bold text-white text-base mb-5">{t('liveActivity.popularRoutes')}</h3>
            <div className="space-y-3">
              {popularRoutes.map((r, i) => (
                <div key={i} className="p-3 bg-white/[0.04] border border-white/[0.06] hover:bg-white/[0.08] transition-colors duration-[var(--duration-fast)] ease-[var(--ease)] cursor-default">
                  <div className="flex items-center gap-2 text-sm font-semibold text-white">
                    <span>{r.from}</span>
                    <ArrowRight className="h-3 w-3 text-[var(--color-orange)]" />
                    <span>{r.to}</span>
                  </div>
                  <p className="text-xs text-white/40 mt-1">{r.info}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="glass p-4 sm:p-6">
            <h3 className="font-display font-bold text-white text-base mb-5">{t('liveActivity.networkActivity')}</h3>
            <div className="space-y-5">
              {networkStats.map((s, i) => (
                <div key={i} className="flex items-center gap-4">
                  <div className="icon-tile">
                    <s.icon className={cn("h-5 w-5", s.color)} strokeWidth={1.7} />
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-white">
                      {isInView ? (
                        <SimpleAnimatedNumber value={s.value} endTimeMs={2000} delayMs={200 + i * 150} />
                      ) : "0"}
                    </div>
                    <p className="text-xs text-white/40 mt-0.5">{s.label}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="glass p-4 sm:p-6">
            <h3 className="font-display font-bold text-white text-base mb-5">{t('liveActivity.tripsDeparting')}</h3>
            <div className="space-y-3">
              {departingSoon.map((trip, i) => (
                <div key={i} className="p-3 bg-white/[0.04] border border-white/[0.06] hover:bg-white/[0.08] transition-colors duration-[var(--duration-fast)] ease-[var(--ease)] flex items-center gap-3">
                  <CalendarDays className="h-4 w-4 text-[var(--color-orange)] shrink-0" />
                  <div>
                    <div className="text-sm font-semibold text-white">
                      {trip.from} → {trip.to}
                    </div>
                    <p className="text-xs text-white/40">{t('liveActivity.departingIn', { days: trip.days })}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Mobile horizontal swipe slider */}
        <div className="md:hidden">
          <div
            ref={scrollRef}
            onScroll={handleScroll}
            className="flex max-w-full snap-x snap-mandatory overflow-x-auto overscroll-x-contain touch-pan-x scrollbar-hide"
            data-allow-horizontal-scroll="true"
            style={{
              WebkitOverflowScrolling: "touch",
              overscrollBehaviorX: "contain",
              overscrollBehaviorInline: "contain",
            }}
          >
            {mobileCards}
          </div>
          {/* Slide indicators — thin outlined segments, no filled shapes */}
          <div className="flex items-center justify-center gap-3 mt-4">
            <span className="font-mono text-[11px] tracking-widest text-white/60 tabular-nums">
              {String(activeSlide + 1).padStart(2, "0")}
            </span>
            <div className="flex items-center">
              {[0, 1, 2].map((i) => (
                <button
                  key={i}
                  onClick={() => {
                    scrollRef.current?.scrollTo({ left: i * (scrollRef.current?.offsetWidth || 0), behavior: "smooth" });
                  }}
                  className="group flex h-11 min-h-0 w-11 min-w-0 items-center justify-center bg-transparent p-0"
                  aria-label={`Slide ${i + 1}`}
                  aria-current={activeSlide === i}
                >
                  <span
                    className={cn(
                      "block h-[2px] transition-all duration-300",
                      activeSlide === i
                        ? "w-8 bg-[var(--color-orange)]"
                        : "w-5 bg-white/25 group-hover:bg-white/50"
                    )}
                  />
                </button>
              ))}
            </div>
            <span className="font-mono text-[11px] tracking-widest text-white/30 tabular-nums">03</span>
          </div>
        </div>

        {/* Recent Activity Feed */}
        <div className="mt-10 glass p-4 flex items-center gap-3 overflow-hidden">
          <span className="text-xs font-semibold text-white/40 uppercase tracking-wider shrink-0">
            {t('liveActivity.recentActivity')}
          </span>
          <div className="h-4 w-px bg-white/10 shrink-0" />
          <p
            className={cn(
              "text-sm text-white/60 transition-all duration-300",
              activityVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-2"
            )}
          >
            {activityFeedItems[currentActivity]}
          </p>
        </div>

        {/* CTA Buttons */}
        <div className="mt-10 flex flex-col sm:flex-row gap-3 justify-center">
          <Button
            className="w-full border-2 border-[var(--color-orange)] bg-transparent text-[var(--color-orange)] font-semibold hover:bg-[var(--color-navy)] font-bold h-12 px-8 text-base shadow-lg"
            asChild
          >
            <Link to={localePath("/quote")}>
              {t('liveActivity.sendShipment')} <ArrowRight className="ms-2 h-5 w-5 rtl:rotate-180" />
            </Link>
          </Button>
          <Button
            className="w-full h-12 px-8 text-base font-semibold border border-white/20 text-white hover:bg-white/10 bg-white/[0.06]"
            asChild
          >
            <Link to={localePath("/for-couriers")}>{t('liveActivity.listYourTrip')}</Link>
          </Button>
        </div>
      </div>

      <style>{`
        @keyframes accentGlow {
          0%, 100% { opacity: 0.3; transform: translateX(-100%); }
          50% { opacity: 1; transform: translateX(100%); }
        }
        .scrollbar-hide::-webkit-scrollbar { display: none; }
        .scrollbar-hide { -ms-overflow-style: none; scrollbar-width: none; }
      `}</style>
    </section>
  );
};

export default LivePlatformActivity;
